import React, { useState, useMemo, useCallback } from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Input } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Toggle } from '@heartinternet/hi-ui';
import { Flex } from '@heartinternet/hi-ui';
import { Form } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import { Table, Column } from '@heartinternet/hi-ui';
import { useNotificationsCog } from './useNotificationsCog';
import './NotificationsPage.css';

export interface NotificationRow {
  packageOid: string;
  domainName: string;
  setupDate: string;
  alertInterval: string;
  created: string;
}

export interface PackageWithoutNotification {
  packageOid: string;
  domainName: string;
  created: string;
}

export interface NotificationsPageProps {
  /** Whether renewal notifications are currently enabled (reseller.use_notifies). */
  useNotifies: boolean;
  /** Customer's account email — used as fallback in the change-email form. */
  customerEmail: string;
  /** Reseller's saved notification email — prefilled in the change-email form. */
  notifyEmail?: string;
  /** Existing renewal notifications, sorted server-side. */
  notifications: NotificationRow[];
  /** Resold packages that don't have a notification yet, sorted server-side. */
  packagesWithoutNotification: PackageWithoutNotification[];
}

const INTERVAL_MONTHS: Record<string, number> = {
  monthly: 1,
  quarterly: 3,
  sixmonthly: 6,
  yearly: 12,
  biyearly: 24,
};

// Display labels for the alert_interval enum. Matches the legacy
// `manage-package-details.tmpl` select options so reseller users see
// consistent terminology across pages.
const INTERVAL_LABEL: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  sixmonthly: 'Six-monthly',
  yearly: 'Yearly',
  biyearly: 'Bi-annually',
};

// "2025-01-15" -> "15-01-2025" (matches ManagePackagesPage's date_added_s format).
// Pass-through for any unexpected shape so we never crash on bad data.
function formatDate(yyyymmdd: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(yyyymmdd)) return yyyymmdd;
  const [y, m, d] = yyyymmdd.split('-');
  return `${d}-${m}-${y}`;
}

// Always anchored to the original setup date, advancing N intervals at a time
// with day-of-month clamped to the last day of the target month. This avoids
// the JS Date overflow bug (Jan 31 + 1 month = Mar 3) by recomputing each
// occurrence rather than stepping `next` repeatedly.
function computeNextRenewal(setupDate: string, alertInterval: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(setupDate)) return '';
  const [y, m, d] = setupDate.split('-').map(Number);
  const step = INTERVAL_MONTHS[alertInterval];
  if (!step) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  /* v8 ignore next -- 10000-iteration cap is a defensive bound; loop always exits well before */
  for (let n = 0; n < 10000; n++) {
    const offsetMonths = m - 1 + step * n;
    const targetYear = y + Math.floor(offsetMonths / 12);
    const targetMonth = ((offsetMonths % 12) + 12) % 12;
    const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate();
    const targetDay = Math.min(d, lastDay);
    const next = new Date(targetYear, targetMonth, targetDay);
    if (next > today) {
      const mm = String(next.getMonth() + 1).padStart(2, '0');
      const dd = String(next.getDate()).padStart(2, '0');
      return `${next.getFullYear()}-${mm}-${dd}`;
    }
  }
  /* v8 ignore start -- unreachable: 10000 intervals == ~833 years of monthly cycle */
  return '';
  /* v8 ignore stop */
}

type CombinedRow =
  ({ kind: 'notification' } & NotificationRow) | ({ kind: 'package' } & PackageWithoutNotification);

export const NotificationsPage: React.FC<NotificationsPageProps> = ({
  useNotifies,
  customerEmail,
  notifyEmail,
  notifications,
  packagesWithoutNotification,
}) => {
  const { renderTrigger, renderPortal } = useNotificationsCog();

  const [sortKey, setSortKey] = useState<string>('domainName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Pre-compute nextRenewal once per row so sorting and rendering share the value.
  type CombinedRowWithDerived = CombinedRow & { nextRenewal: string };
  const combined: CombinedRowWithDerived[] = useMemo(
    () => [
      ...notifications.map<CombinedRowWithDerived>((n) => ({
        kind: 'notification',
        ...n,
        nextRenewal: computeNextRenewal(n.setupDate, n.alertInterval),
      })),
      ...packagesWithoutNotification.map<CombinedRowWithDerived>((p) => ({
        kind: 'package',
        ...p,
        nextRenewal: '',
      })),
    ],
    [notifications, packagesWithoutNotification]
  );

  const sorted = useMemo(() => {
    const sortValue = (row: CombinedRowWithDerived): string => {
      switch (sortKey) {
        case 'domainName':
          return row.domainName.toLowerCase();
        case 'nextRenewal':
          return row.nextRenewal;
        case 'alertInterval': {
          // Sort by interval frequency (months) rather than by label so the
          // visible order matches the time period (Monthly → Bi-annually),
          // not alphabetical (Bi-annually → Yearly).
          if (row.kind !== 'notification') return '';
          const months = INTERVAL_MONTHS[row.alertInterval];
          return months ? String(months).padStart(3, '0') : '999';
        }
        case 'created':
          return row.created;
        /* v8 ignore next 2 -- unreachable: sortKey is always one of the keys above */
        default:
          return '';
      }
    };
    return [...combined].sort((a, b) => {
      const cmp = sortValue(a).localeCompare(sortValue(b));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [combined, sortKey, sortDirection]);

  const paginated = useMemo(
    () => sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sorted, currentPage, pageSize]
  );

  const handleSort = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDirection(direction);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const columns: Column<CombinedRowWithDerived>[] = [
    {
      key: 'domainName',
      label: 'Domain Name',
      sortable: true,
      width: 'auto',
      render: (_value, row) => (
        <Text variant="body-small" inline>
          {row.domainName}
        </Text>
      ),
    },
    {
      key: 'nextRenewal',
      label: 'Renews on',
      sortable: true,
      width: '130px',
      render: (_value, row) => (
        <Text variant="body-small" inline>
          {formatDate(row.nextRenewal)}
        </Text>
      ),
    },
    {
      key: 'alertInterval',
      label: 'Renewal period',
      sortable: true,
      align: 'center',
      width: '140px',
      render: (_value, row) => (
        <Text variant="body-small" inline>
          {row.kind === 'notification' ? INTERVAL_LABEL[row.alertInterval] || '' : ''}
        </Text>
      ),
    },
    {
      key: 'created',
      label: 'Created',
      sortable: true,
      width: '130px',
      render: (_value, row) => (
        <Text variant="body-small" inline>
          {formatDate(row.created)}
        </Text>
      ),
    },
    {
      key: 'packageOid',
      label: '',
      align: 'right',
      width: '60px',
      render: (_value, row) => renderTrigger(row),
    },
  ];

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const form = e.currentTarget.form;
    /* v8 ignore start -- form is always set because the Toggle is rendered inside a <form> */
    if (!form) return;
    /* v8 ignore stop */
    form.submit();
  };

  return (
    <div className="hi-ui-notifications-page">
      <Text variant="heading-1">Renewal Notifications</Text>
      <Text variant="body">
        Here you can turn on the ability to receive renewal notification emails for your
        customers&rsquo; packages.
      </Text>
      <Text variant="body">
        You will be emailed 1 month in advance for yearly renewals and 7 days in advance for all
        other renewal periods.
      </Text>

      <Text variant="heading-2">Notification Email Address</Text>
      <Text variant="body">
        Set the email address you would like these notification emails to be sent to.
      </Text>

      <Form action="/manage/notifications.cgi" method="post">
        <input type="hidden" name="enable" value="1" />
        <FormField label="Notification email">
          <Input type="text" name="notify_email" defaultValue={notifyEmail || customerEmail} />
        </FormField>
        <div className="hi-ui-notifications-page__submit">
          <Button type="submit" variant="primary">
            Update
          </Button>
        </div>
      </Form>

      <div className="hi-ui-notifications-page__table-section">
        <Flex
          direction="row"
          align="center"
          justify="between"
          wrap
          className="hi-ui-notifications-page__table-header"
        >
          <Text variant="heading-2">Hosting Package Notifications</Text>
          <form action="/manage/notifications.cgi" method="post">
            <input type="hidden" name="enable" value={useNotifies ? '0' : '1'} />
            <input type="hidden" name="notify_email" value={notifyEmail || customerEmail} />
            <Toggle
              name="notifications_enabled"
              checked={useNotifies}
              onChange={handleToggle}
              label={useNotifies ? 'Enabled' : 'Disabled'}
              labelPosition="left"
              aria-label="Toggle renewal notifications"
            />
          </form>
        </Flex>

        {useNotifies && combined.length > 0 ? (
          <>
            <Text variant="body">
              Below is a list of the setup dates and intervals of all notifications you have
              currently set up.
            </Text>
            <Table<CombinedRowWithDerived>
              columns={columns}
              data={paginated}
              getRowKey={(row) => row.packageOid}
              ariaLabel="Hosting package notifications"
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={handleSort}
              pagination={{
                currentPage,
                pageSize,
                totalItems: sorted.length,
                pageSizeOptions: [25, 50, 100],
              }}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </>
        ) : (
          <Text variant="body">
            {useNotifies
              ? 'You do not currently have any notifications set up, the next time you activate a web hosting account simply set a renewal date.'
              : 'Renewal notifications are currently disabled. Use the toggle above to enable them.'}
          </Text>
        )}
      </div>

      {renderPortal()}
    </div>
  );
};
